import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import HubIcon from '@mui/icons-material/Hub';
import PersonIcon from '@mui/icons-material/Person';

import { CustomTitle } from '../../../common/CustomTitle/CustomTitle';
import { GoBack } from '../../../common/GoBack/GoBack';
import { startUsersActive } from '../../../../reducers/users/usersActions';
import { fetchConToken } from '../../../../helpers/fetch';


export const UserIntegrationsApiPermissionsScreen = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.users.active);
  const activeContract = useSelector((state) => state.contracts.activeContract);
  const contractId = activeContract?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [operations, setOperations] = useState([]);
  const [active, setActive] = useState(false);
  const [granted, setGranted] = useState(new Set());

  useEffect(() => {
    if (id && contractId && (!user || user.id !== parseInt(id))) {
      dispatch(startUsersActive(id));
    }
  }, [id, user, contractId, dispatch]);

  const applyResponse = (data) => {
    const ops = data?.operations || [];
    setIntegration(data?.integration || null);
    setOperations(ops);
    setActive(Boolean(data?.integration?.active));
    setGranted(new Set(ops.filter((op) => op.granted).map((op) => op.id)));
  };

  const fetchPermissions = useCallback(async () => {
    const res = await fetchConToken(`contract/${contractId}/integrations/users/${id}/permissions`);
    if (!res.ok) throw res.status;
    return res.json();
  }, [contractId, id]);

  const loadPermissions = useCallback(async () => {
    if (!contractId || !id) return;
    setLoading(true);
    setError(null);
    try {
      applyResponse(await fetchPermissions());
    } catch (err) {
      setError(`No se pudieron cargar los permisos de la API de integraciones (${err}).`);
    } finally {
      setLoading(false);
    }
  }, [contractId, id, fetchPermissions]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const initialActive = Boolean(integration?.active);
  const initialGranted = useMemo(
    () => new Set(operations.filter((op) => op.granted).map((op) => op.id)),
    [operations]
  );

  const hasChanges =
    active !== initialActive ||
    granted.size !== initialGranted.size ||
    [...granted].some((opId) => !initialGranted.has(opId));

  // Al quitar el permiso general se quitan también todas las operaciones
  const handleActiveChange = (checked) => {
    setActive(checked);
    if (!checked) setGranted(new Set());
  };

  const toggle = (opId) => {
    setGranted((prev) => {
      const next = new Set(prev);
      next.has(opId) ? next.delete(opId) : next.add(opId);
      return next;
    });
  };

  const handleDiscard = () => {
    setActive(initialActive);
    setGranted(new Set(initialGranted));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchConToken(
        `contract/${contractId}/integrations/users/${id}/permissions`,
        { active, operations: [...granted] },
        'PUT'
      );
      if (!res.ok) throw res.status;
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: `No se pudieron guardar los permisos (${err}).` });
      setSaving(false);
      return;
    }

    Swal.fire({ icon: 'success', title: 'Permisos guardados', timer: 1500, showConfirmButton: false });

    // El PUT solo devuelve { msg: 'ok' }: se recarga el estado guardado desde el GET
    try {
      applyResponse(await fetchPermissions());
    } catch (err) {
      setError(`Permisos guardados, pero no se pudieron recargar (${err}).`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ padding: 2 }}>
      <Grid container spacing={1} className='animate__animated animate__fadeIn animate__fast'>
        <Grid item xs={12} sx={{ display: 'flex' }}>
          <GoBack />
          <CustomTitle
            title='Permisos API de integraciones'
            icon={
              <Avatar>
                <HubIcon />
              </Avatar>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={
                <Avatar>
                  <PersonIcon />
                </Avatar>
              }
              title={user?.name}
              subheader={user?.email}
              action={
                !loading && !error && (
                  <Box sx={{ display: 'flex', gap: 1, pt: 0.5, pr: 1 }}>
                    <Button disabled={!hasChanges || saving} onClick={handleDiscard}>
                      Descartar
                    </Button>
                    <Button variant='contained' disabled={!hasChanges || saving} onClick={handleSave}>
                      {saving ? <CircularProgress size={22} /> : 'Guardar'}
                    </Button>
                  </Box>
                )
              }
            />
            <Divider />
            <CardContent sx={{ paddingTop: 0, '&:last-child': { paddingBottom: 6 } }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity='error' action={<Button onClick={loadPermissions}>Reintentar</Button>}>
                  {error}
                </Alert>
              ) : (
                <>
                  <List disablePadding>
                    <ListItem
                      secondaryAction={
                        <Switch
                          edge='end'
                          checked={active}
                          onChange={(e) => handleActiveChange(e.target.checked)}
                          disabled={saving}
                        />
                      }
                    >
                      <ListItemText
                        primary={`Permitir uso de la API de integraciones en ${activeContract?.name || 'el contrato'}`}
                        primaryTypographyProps={{ fontWeight: 'bold' }}
                        secondary='Si se desactiva, el usuario no puede usar la API y se quitan todas sus operaciones.'
                      />
                    </ListItem>
                  </List>
                  <Divider />
                  <List
                    subheader={<ListSubheader>Operaciones permitidas</ListSubheader>}
                    sx={{ opacity: active ? 1 : 0.5, transition: 'opacity 0.2s' }}
                  >
                    {operations.length === 0 && (
                      <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
                        No hay operaciones disponibles.
                      </Typography>
                    )}
                    {operations.map((op) => (
                      <ListItem
                        key={op.id}
                        divider
                        secondaryAction={
                          <Switch
                            edge='end'
                            checked={granted.has(op.id)}
                            onChange={() => toggle(op.id)}
                            disabled={saving || !active}
                          />
                        }
                      >
                        <ListItemText
                          primary={op.description}
                          secondary={op.code}
                          secondaryTypographyProps={{ fontFamily: 'monospace' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
